import React, { useState, useEffect } from "react";
import useBogsGisPortalAuthorize from "@cores/security/hooks/useBogsGisPortalAuthorize";

// Helper to resolve a testable REST URL for any item type
const getItemTargetUrl = (item, portal) => {
  if (!item) return "";

  if (item.url) return item.url;

  if (item.id) {
    const restBase =
      portal?.restUrl || (portal?.url ? `${portal.url}/sharing/rest` : "");

    if (restBase) {
      return `${restBase}/content/items/${item.id}`;
    }
  }

  return "";
};

// Helper to parse embedded JSON from raw message string or object
const parseNiogemsPayload = (message) => {
  if (!message) return null;

  // 1. If it's already an object, return it directly
  if (typeof message === "object") return message;

  // 2. Extract JSON string if embedded inside text
  if (typeof message === "string") {
    const jsonStartIndex = message.indexOf("{");
    const jsonEndIndex = message.lastIndexOf("}");

    if (jsonStartIndex !== -1 && jsonEndIndex !== -1) {
      try {
        const jsonSubstring = message.substring(jsonStartIndex, jsonEndIndex + 1);
        return JSON.parse(jsonSubstring);
      } catch (e) {
        console.warn("Could not parse payload JSON:", e);
      }
    }
  }

  return null;
};

export default function PgPubTestAuthorization() {
  const {
    loading,
    signedIn,
    portalUser,
    portal,
    niogemsAuth,
    niogemsLoading,
    statusMsg,
    fetchNiogemsToken,
    logout,
    privileges,
    userGroups,
    items,
    itemsLoading,
    queryPortalItems,
    layerCheckResult,
    layerChecking,
    testServiceAccess
  } = useBogsGisPortalAuthorize(false);

  const [selectedType, setSelectedType] = useState('type:"Web Map"');
  const [customUrl, setCustomUrl] = useState("");

  const isNiogemsApproved =
    niogemsAuth?.status === "SUCCESS" ||
    niogemsAuth?.status === "DEV_BYPASS" ||
    (typeof niogemsAuth?.status === "string" &&
      (niogemsAuth.status.includes("Active") ||
        niogemsAuth.status.includes("Success") ||
        niogemsAuth.status.includes("Approved")));

  const isNiogemsDenied = niogemsAuth && !isNiogemsApproved;

  useEffect(() => {
    if (signedIn && portal) {
      queryPortalItems(selectedType);
    }
  }, [signedIn, portal, selectedType, queryPortalItems]);

  if (loading) {
    return (
      <div style={{ padding: "20px" }}>
        <strong>Checking Portal identity status...</strong>
      </div>
    );
  }

  if (!signedIn) {
    return (
      <div style={{ padding: "20px", color: "red" }}>
        <strong>Not signed in to Portal. Access Denied.</strong>
      </div>
    );
  }

  const columnCardStyle = {
    background: "#ffffff",
    border: "1px solid #ddd",
    borderRadius: "8px",
    padding: "20px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
    display: "flex",
    flexDirection: "column",
    gap: "15px"
  };

  // Parse payload properties for the success section
  const parsedNiogemsData = isNiogemsApproved
    ? parseNiogemsPayload(niogemsAuth?.message)
    : null;

  return (
    <div
      style={{
        padding: "20px",
        fontFamily: "sans-serif",
        maxWidth: "100%",
        margin: "0 auto"
      }}
    >
      {/* 2-COLUMN SIDE-BY-SIDE GRID */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "24px",
          alignItems: "start"
        }}
      >
        {/* LEFT COLUMN: BOGS AUTHORIZATION */}
        <section style={columnCardStyle}>
          <div>
            <h2 style={{ marginTop: 0, fontSize: "20px" }}>
              BOGS Authorization
            </h2>
            <p style={{ fontSize: "12px", color: "#666", margin: "2px 0 8px 0" }}>
              From BOGS ArcGIS Portal, Geoplatform
            </p>
            <p style={{ color: "#555", fontSize: "14px", margin: 0 }}>
              <strong>Status:</strong> {statusMsg}
            </p>
          </div>
          <hr style={{ margin: 0, borderColor: "#eee" }} />

          {/* USER PROFILE & GROUPS */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "12px"
            }}
          >
            <div
              style={{
                background: "#f5f7fa",
                padding: "12px",
                borderRadius: "6px"
              }}
            >
              <h4 style={{ margin: "0 0 8px 0" }}>Profile & Privileges</h4>
              <p style={{ margin: "4px 0", fontSize: "13px" }}>
                <strong>Name:</strong> {portalUser?.fullName}
              </p>
              <p style={{ margin: "4px 0", fontSize: "13px" }}>
                <strong>Email:</strong>{" "}
                <span style={{ color: "blue", fontWeight: "bold" }}>
                  {portalUser?.email}
                </span>
              </p>
              <p style={{ margin: "4px 0", fontSize: "13px" }}>
                <strong>Provider:</strong> {portalUser?.provider}
              </p>
              <h5 style={{ margin: "8px 0 4px 0" }}>
                Privileges ({privileges?.length || 0}):
              </h5>
              <div
                style={{
                  maxHeight: "100px",
                  overflowY: "auto",
                  background: "#fff",
                  padding: "6px",
                  border: "1px solid #ccc",
                  borderRadius: "4px"
                }}
              >
                <ul
                  style={{ margin: 0, paddingLeft: "16px", fontSize: "12px" }}
                >
                  {privileges?.map((p, idx) => (
                    <li key={idx}>
                      <code>{p}</code>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div
              style={{
                background: "#f5f7fa",
                padding: "12px",
                borderRadius: "6px"
              }}
            >
              <h4 style={{ margin: "0 0 8px 0" }}>
                Groups ({userGroups?.length || 0})
              </h4>
              <div
                style={{
                  maxHeight: "180px",
                  overflowY: "auto",
                  background: "#fff",
                  padding: "6px",
                  border: "1px solid #ccc",
                  borderRadius: "4px"
                }}
              >
                <ul
                  style={{ margin: 0, paddingLeft: "16px", fontSize: "12px" }}
                >
                  {userGroups?.map((g) => (
                    <li key={g.id}>
                      <strong>{g.title}</strong> (Owner: {g.owner})
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* QUERY PORTAL CONTENT */}
          <div
            style={{
              padding: "15px",
              border: "1px solid #ddd",
              borderRadius: "6px"
            }}
          >
            <h4 style={{ margin: "0 0 4px 0" }}>Query Accessible Content</h4>
            <p style={{ fontSize: "12px", color: "#666", margin: "0 0 10px 0" }}>
              Select an item category to run <code>portal.queryItems()</code>.
            </p>

            <div
              style={{
                display: "flex",
                gap: "8px",
                marginBottom: "12px",
                flexWrap: "wrap"
              }}
            >
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                style={{
                  padding: "6px 10px",
                  fontSize: "13px",
                  borderRadius: "4px",
                  flex: 1
                }}
              >
                <option value='type:"Web Map"'>Web Maps</option>
                <option value='type:"Feature Service"'>
                  Feature Services (Layers)
                </option>
                <option value='type:"Map Service"'>Map Services</option>
                <option value='type:"Vector Tile Service"'>
                  Vector Tile Services
                </option>
                <option value="owner:me">My Items Only</option>
              </select>

              <button
                onClick={() => queryPortalItems(selectedType)}
                disabled={itemsLoading}
                style={{
                  padding: "6px 12px",
                  background: "#007acc",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "13px"
                }}
              >
                {itemsLoading ? "Searching..." : "Refresh"}
              </button>
            </div>

            {/* RESULTS TABLE */}
            <div
              style={{
                maxHeight: "200px",
                overflowY: "auto",
                border: "1px solid #ddd"
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "12px",
                  textAlign: "left"
                }}
              >
                <thead
                  style={{ background: "#eee", position: "sticky", top: 0 }}
                >
                  <tr>
                    <th style={{ padding: "6px" }}>Title</th>
                    <th style={{ padding: "6px" }}>Type</th>
                    <th style={{ padding: "6px" }}>Access</th>
                    <th style={{ padding: "6px" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items?.length === 0 ? (
                    <tr>
                      <td
                        colSpan="4"
                        style={{ padding: "10px", textAlign: "center" }}
                      >
                        No items returned.
                      </td>
                    </tr>
                  ) : (
                    items?.map((item) => {
                      const targetUrl = getItemTargetUrl(item, portal);

                      return (
                        <tr
                          key={item.id}
                          style={{ borderBottom: "1px solid #eee" }}
                        >
                          <td style={{ padding: "6px" }}>{item.title}</td>
                          <td style={{ padding: "6px" }}>{item.type}</td>
                          <td style={{ padding: "6px" }}>
                            <code>{item.access}</code>
                          </td>
                          <td style={{ padding: "6px" }}>
                            {targetUrl ? (
                              <button
                                onClick={() => {
                                  setCustomUrl(targetUrl);
                                  testServiceAccess(targetUrl);
                                }}
                                style={{
                                  padding: "3px 6px",
                                  fontSize: "11px",
                                  backgroundColor: "#007acc",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "3px",
                                  cursor: "pointer"
                                }}
                              >
                                Test
                              </button>
                            ) : (
                              <span style={{ color: "#aaa", fontSize: "10px" }}>
                                N/A
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* DIRECT LAYER ACCESS CHECKER */}
          <div
            style={{
              padding: "15px",
              border: "1px solid #ddd",
              borderRadius: "6px",
              background: "#fafafa"
            }}
          >
            <h4 style={{ margin: "0 0 4px 0" }}>Test Specific Layer URL</h4>
            <p style={{ fontSize: "12px", color: "#666", margin: "0 0 10px 0" }}>
              Paste a feature layer URL or select an item above:
            </p>

            <div style={{ display: "flex", gap: "8px" }}>
              <input
                type="text"
                placeholder="https://.../FeatureServer/0"
                value={customUrl}
                onChange={(e) => setCustomUrl(e.target.value)}
                style={{
                  flex: 1,
                  padding: "6px 8px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                  fontSize: "12px"
                }}
              />
              <button
                onClick={() => testServiceAccess(customUrl)}
                disabled={layerChecking || !customUrl}
                style={{
                  padding: "6px 12px",
                  background: "#28a745",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "12px"
                }}
              >
                {layerChecking ? "Validating..." : "Check"}
              </button>
            </div>

            {layerCheckResult && (
              <div
                style={{
                  marginTop: "10px",
                  padding: "10px",
                  borderRadius: "4px",
                  background: layerCheckResult.success ? "#e6f4ea" : "#fce8e6",
                  fontSize: "12px"
                }}
              >
                {layerCheckResult.success ? (
                  <div>
                    <p style={{ color: "green", margin: "0 0 4px 0" }}>
                      <strong>✔ Access Authorized!</strong>
                    </p>
                    <p style={{ margin: "2px 0" }}>
                      <strong>Title:</strong> {layerCheckResult.title}
                    </p>
                    <p style={{ margin: "2px 0" }}>
                      <strong>Geometry:</strong> {layerCheckResult.geometryType}
                    </p>
                    <p style={{ margin: "2px 0" }}>
                      <strong>Editing Rights:</strong>{" "}
                      {layerCheckResult.canEdit ? "Yes" : "Read-Only"}
                    </p>
                  </div>
                ) : (
                  <div>
                    <p style={{ color: "red", margin: "0 0 4px 0" }}>
                      <strong>❌ Access Denied / Error</strong>
                    </p>
                    <p style={{ margin: 0 }}>{layerCheckResult.error}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* RIGHT COLUMN: NIOGEMS AUTHORIZATION */}
        <section style={columnCardStyle}>
          <div>
            <h2 style={{ marginTop: 0, fontSize: "20px" }}>
              NIOGEMS Authorization
            </h2>
            <p style={{ fontSize: "12px", color: "#666", margin: "2px 0 0 0" }}>
              From NIOGEMS API
            </p>
          </div>
          <hr style={{ margin: 0, borderColor: "#eee" }} />

          <p style={{ fontSize: "13px", color: "#555", margin: 0 }}>
            Click below to verify authorization status with the NIOGEMS backend.
          </p>

          <div>
            <button
              onClick={fetchNiogemsToken}
              disabled={niogemsLoading || !portalUser?.email}
              style={{
                padding: "8px 16px",
                fontSize: "14px",
                backgroundColor: "#007acc",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontWeight: "bold"
              }}
            >
              {niogemsLoading ? "Verifying Status..." : "Check Status Payload"}
            </button>
          </div>

          <div style={{ marginTop: "5px" }}>
            {!niogemsAuth && !niogemsLoading && (
              <p style={{ color: "#666", fontSize: "13px", margin: 0 }}>
                <em>No handshake requested yet. Ready when you are.</em>
              </p>
            )}

            {niogemsAuth && (
              <div
                style={{
                  padding: "12px",
                  background: "#f9f9f9",
                  borderLeft: `4px solid ${
                    isNiogemsApproved ? "green" : "red"
                  }`,
                  borderRadius: "0 4px 4px 0",
                  fontSize: "13px"
                }}
              >
                {isNiogemsDenied && (
                  <div style={{ color: "red" }}>
                    <p style={{ margin: "0 0 6px 0" }}>
                      ❌ <strong>Access Denied or Intercepted.</strong>
                    </p>
                    <p style={{ margin: "2px 0" }}>
                      <strong>Status Category:</strong>{" "}
                      <code>{niogemsAuth.status}</code>
                    </p>
                    <p style={{ margin: "2px 0" }}>
                      <strong>Server Message:</strong>{" "}
                      {typeof niogemsAuth.message === "object"
                        ? JSON.stringify(niogemsAuth.message)
                        : niogemsAuth.message}
                    </p>
                  </div>
                )}

                {isNiogemsApproved && (
                  <div>
                    <p style={{ margin: "0 0 6px 0", color: "green" }}>
                      ✔ <strong>Success! Active Dev Bypass Intercepted.</strong>
                    </p>
                    <p style={{ margin: "2px 0", color: "#333" }}>
                      <strong>Status Rule:</strong>{" "}
                      <code>{niogemsAuth.status}</code>
                    </p>

                    {/* PARSED PROPERTIES IN GREEN */}
                    {parsedNiogemsData && (
                      <div
                        style={{
                          marginTop: "12px",
                          padding: "12px",
                          background: "#e6f4ea",
                          border: "1px solid #b7e1cd",
                          borderRadius: "6px",
                          color: "#137333"
                        }}
                      >
                        <h4 style={{ margin: "0 0 8px 0", color: "#0d652d" }}>
                          Parsed Payload Properties
                        </h4>
                        <ul style={{ margin: 0, paddingLeft: "18px" }}>
                          {Object.entries(parsedNiogemsData).map(([key, value]) => (
                            <li key={key} style={{ margin: "4px 0" }}>
                              <strong style={{ color: "#0d652d" }}>{key}:</strong>{" "}
                              <code
                                style={{
                                  background: "#d4edda",
                                  padding: "2px 5px",
                                  borderRadius: "3px",
                                  color: "#155724"
                                }}
                              >
                                {value === null
                                  ? "null"
                                  : typeof value === "object"
                                  ? JSON.stringify(value)
                                  : String(value)}
                              </code>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* RAW PAYLOAD DISPLAY FOR DEV */}
                    <div
                      style={{
                        background: "#fff",
                        border: "1px solid #ddd",
                        padding: "8px",
                        borderRadius: "4px",
                        marginTop: "12px",
                        color: "#333"
                      }}
                    >
                      <p
                        style={{
                          margin: "0 0 6px 0",
                          fontSize: "12px",
                          fontWeight: "600"
                        }}
                      >
                        Raw Stream Payload Message:
                      </p>
                      <pre
                        style={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-all",
                          background: "#222",
                          color: "#fff",
                          padding: "10px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          margin: 0,
                          maxHeight: "250px",
                          overflowY: "auto"
                        }}
                      >
                        {typeof niogemsAuth.message === "object"
                          ? JSON.stringify(niogemsAuth.message, null, 2)
                          : niogemsAuth.message}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      </div>

      {/* FOOTER / LOGOUT */}
      <div style={{ marginTop: "24px", textAlign: "right" }}>
        <button
          onClick={logout}
          style={{
            padding: "8px 16px",
            background: "#cc3333",
            border: "none",
            borderRadius: "4px",
            color: "white",
            cursor: "pointer",
            fontWeight: "bold"
          }}
        >
          Logout Session
        </button>
      </div>
    </div>
  );
}